import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Fade,
  Backdrop,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export type ConfirmPopupMode = "swageazy" | "swayy";

interface ConfirmPopupProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
  confirmDisabled?: boolean;
  error?: string | null;
  children?: React.ReactNode;
  /** Visual theme. Defaults to swageazy (original styling). Use swayy for Swayy brand colors. */
  mode?: ConfirmPopupMode;
  zIndex?: number;
}

const SWAYY = {
  pine: "#0BB6AE",
  pineHover: "#0AA69F",
  deepGreen: "#005154",
  textSecondary: "#4A6B6D",
  border: "#C9E4EC",
  white: "#FFFFFF",
  danger: "#DC2626",
  font: "'DM Sans', system-ui, -apple-system, sans-serif",
  radius: "16px",
} as const;

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
  open,
  title,
  message,
  confirmText = "Apply",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
  loading = false,
  confirmDisabled = false,
  error = null,
  children,
  mode = "swageazy",
  zIndex = 2000,
}) => {
  if (mode === "swayy") {
    return (
      <Dialog
        open={open}
        onClose={loading ? undefined : onCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: SWAYY.radius,
            border: `1px solid ${SWAYY.border}`,
            bgcolor: SWAYY.white,
            fontFamily: SWAYY.font,
          },
        }}
        sx={{ zIndex }}
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            fontFamily: SWAYY.font,
            fontSize: 18,
            fontWeight: 700,
            color: SWAYY.deepGreen,
            pb: 1,
          }}
        >
          {title || "Are you sure?"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="confirm-dialog-description"
            sx={{
              fontFamily: SWAYY.font,
              fontSize: 14,
              color: SWAYY.textSecondary,
              lineHeight: 1.5,
            }}
          >
            {message || "This action cannot be undone."}
          </DialogContentText>

          {children ? (
            <Box sx={{ mt: 2, width: "100%" }}>{children}</Box>
          ) : null}

          {error ? (
            <Typography
              sx={{
                mt: 2,
                fontSize: 13,
                fontWeight: 500,
                color: SWAYY.danger,
                fontFamily: SWAYY.font,
                wordBreak: "break-word",
              }}
            >
              {error}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={onCancel}
            disabled={loading}
            sx={{
              fontFamily: SWAYY.font,
              textTransform: "none",
              fontWeight: 600,
              color: SWAYY.deepGreen,
              border: `1px solid ${SWAYY.border}`,
              borderRadius: "10px",
              px: 2,
              "&:hover": {
                bgcolor: "#E5F9F7",
                borderColor: SWAYY.pine,
              },
            }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
            variant="contained"
            sx={{
              fontFamily: SWAYY.font,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: SWAYY.pine,
              color: SWAYY.white,
              borderRadius: "10px",
              px: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: SWAYY.pineHover,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: SWAYY.border,
                color: SWAYY.textSecondary,
              },
            }}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // swageazy — original ConfirmPopup styling (unchanged)
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          maxWidth: { xs: "90vw", sm: "400px" },
          width: { xs: "90vw", sm: "400px" },
          margin: { xs: "16px", sm: "32px" },
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        },
      }}
      sx={{
        zIndex,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background:
              "linear-gradient(90deg, #ff9800 0%, #f57c00 50%, #ff9800 100%)",
          },
        }}
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            padding: { xs: "24px 24px 16px", sm: "32px 32px 20px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            "& .MuiDialogTitle-root": {
              padding: 0,
            },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#fff3e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
                "50%": {
                  boxShadow: "0 4px 20px rgba(255, 152, 0, 0.5)",
                },
                "100%": {
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
              },
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: 32,
                color: "#ff9800",
                filter: "drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3))",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#e65100",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              letterSpacing: "-0.02em",
            }}
          >
            {title || "Are you sure?"}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
            textAlign: "center",
            minHeight: "auto",
            width: "100%",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1rem" },
              color: "#424242",
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: "100%",
              wordBreak: "break-word",
            }}
          >
            {message || "This action cannot be undone."}
          </Typography>
          {children ? (
            <Box sx={{ mt: 2, width: "100%", textAlign: "left" }}>
              {children}
            </Box>
          ) : null}
          {error ? (
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                color: "#DC2626",
                lineHeight: 1.5,
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              {error}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={loading}
            sx={{
              color: "#6b7280",
              borderColor: "#d1d5db",
              backgroundColor: "#ffffff",
              padding: { xs: "10px 22px", sm: "12px 26px" },
              fontWeight: 600,
              borderRadius: "12px",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              textTransform: "none",
              minWidth: { xs: "120px", sm: "140px" },
              height: { xs: "44px", sm: "48px" },
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={loading || confirmDisabled}
            sx={{
              backgroundColor: "#ff9800",
              color: "#ffffff",
              padding: { xs: "12px 24px", sm: "14px 28px" },
              fontWeight: 600,
              borderRadius: "12px",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              textTransform: "none",
              minWidth: { xs: "120px", sm: "140px" },
              height: { xs: "44px", sm: "48px" },
              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#f57c00",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(255, 152, 0, 0.4)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.3)",
              },
            }}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ConfirmPopup;
